import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router'

import { useNotifications } from 'app/application/application'
import { Form } from 'app/common/presentation'
import { useConnections } from 'app/connections/application'
import { CriteoAdvertiser } from 'app/connections/types'

import { Button } from 'components'

import Accounts from '../Accounts'
import Authorization from '../Authorization'
import setupStyles from '../Setup.module.scss'
import { StepContext } from './StepWithConnectionAndAccounts'

const CriteoSettings = () => {
  const navigate = useNavigate()
  const { createNotification, te } = useNotifications()
  const { accounts, connection: connection } = useOutletContext<StepContext<CriteoAdvertiser>>()
  const { saveConnection: saveConnection } = useConnections()
  const { t } = useTranslation('connections')

  const [adSetId, setAdSetId] = useState<string>('')
  const [gumCallerId, setGumCallerId] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const handleSave = useCallback(() => {
    setIsSaving(true)

    const connectionConfig = {
      advertisers: accounts,
      adSetId,
      gumCallerId,
    }

    void saveConnection(connection, connectionConfig)
      .then((result) => {
        if (result.ok) {
          createNotification({
            message: t('update.success.message', {
              name: t(`available.${connection.name}.displayName`),
            }),
            title: t('update.success.title'),
            type: 'success',
          })

          void navigate(`/connections/${connection.name}/setup`, { replace: true })
        } else {
          createNotification({
            message: result.val.message,
            title: te(`http.${result.val.code}`),
            type: 'error',
          })
        }
      })
      .finally(() => setIsSaving(false))
  }, [
    accounts,
    adSetId,
    createNotification,
    gumCallerId,
    navigate,
    saveConnection,
    connection,
    t,
    te,
  ])

  return (
    <div>
      <div className={setupStyles.form}>
        <Authorization authorizationState={'VALID'} connection={connection} />
        <Accounts accounts={accounts} connectionName={connection.name} />
        <Form.InputFormRow
          description={t(`setup.${connection.name}.config.adSetId.description`)}
          onChange={setAdSetId}
          placeholder={t(`setup.${connection.name}.config.adSetId.title`)}
          title={t(`setup.${connection.name}.config.adSetId.title`)}
          value={adSetId}
        />
        <Form.InputFormRow
          description={t(`setup.${connection.name}.config.gumCallerId.description`)}
          onChange={setGumCallerId}
          placeholder={t(`setup.${connection.name}.config.gumCallerId.title`)}
          title={t(`setup.${connection.name}.config.gumCallerId.title`)}
          value={gumCallerId}
        />
      </div>
      <div className={setupStyles.actions}>
        <Button onClick={() => void navigate(-1)} variant="secondary">
          {t('setup.actions.back')}
        </Button>
        <Button
          disabled={!adSetId || !gumCallerId}
          loading={isSaving}
          onClick={(e) => {
            e.stopPropagation()

            handleSave()
          }}
        >
          {t('setup.actions.finish')}
        </Button>
      </div>
    </div>
  )
}

export default CriteoSettings
